/**
 * Utilitário para validação de URLs do YouTube
 */

import youtubeService from "services/YouTubeService";

/**
 * Extrai o ID do vídeo de uma URL do YouTube
 * @param {string} url - URL do YouTube
 * @returns {string|null} - ID do vídeo ou null se inválido
 */
export const extractYouTubeVideoId = (url) => {
	if (!url) return null;

	const patterns = [
		// youtube.com/watch?v=VIDEO_ID
		/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
		// youtu.be/VIDEO_ID
		/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
		// youtube.com/embed/VIDEO_ID
		/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
		// youtube.com/v/VIDEO_ID
		/(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
		// youtube.com/shorts/VIDEO_ID
		/(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
	];

	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match && match[1]) {
			return match[1];
		}
	}
	return null;
};

/**
 * Valida se uma URL é uma URL válida do YouTube
 * @param {string} url - URL para validar
 * @returns {boolean} - true se válida
 */
export const isValidYouTubeUrl = (url) => {
	if (!url) return false;

	try {
		const urlObj = new URL(url);
		const hostname = urlObj.hostname.toLowerCase();

		// Verificar se é do YouTube
		if (
			![
				"youtube.com",
				"www.youtube.com",
				"youtu.be",
				"m.youtube.com",
			].includes(hostname)
		) {
			return false;
		}

		// Extrair e validar video ID
		const videoId = extractYouTubeVideoId(url);
		if (!videoId) return false;

		// Video ID do YouTube deve ter exatamente 11 caracteres alfanuméricos, _ ou -
		const videoIdPattern = /^[a-zA-Z0-9_-]{11}$/;
		return videoIdPattern.test(videoId);
	} catch (error) {
		return false;
	}
};

/**
 * Normaliza uma URL do YouTube para o formato padrão
 * @param {string} url - URL do YouTube
 * @returns {string|null} - URL normalizada ou null se inválida
 */
export const normalizeYouTubeUrl = (url) => {
	const videoId = extractYouTubeVideoId(url);
	if (!videoId) return null;

	// Se for shorts, manter o formato de shorts
	if (url.includes("/shorts/")) {
		return `https://www.youtube.com/shorts/${videoId}`;
	}

	return `https://www.youtube.com/watch?v=${videoId}`;
};

/**
 * Tipos de erro possíveis ao verificar disponibilidade do vídeo
 */
export const YouTubeVideoErrorType = {
	PRIVATE: "private",
	NOT_FOUND: "not_found",
	UNAVAILABLE: "unavailable",
	TOO_LONG: "too_long",
	NETWORK_ERROR: "network_error",
	UNKNOWN: "unknown",
};

/**
 * Duração máxima permitida em segundos (10 minutos) — usada para exercícios
 */
export const MAX_VIDEO_DURATION_SECONDS = 10 * 60;

/**
 * Duração máxima para vídeos de material extra (15 minutos)
 */
export const MAX_MATERIAL_VIDEO_DURATION_SECONDS = 15 * 60;

/**
 * Formatar duração em minutos e segundos
 * @param {number} seconds - Duração em segundos
 * @returns {string} - Duração formatada (ex: "5:30")
 */
export const formatDuration = (seconds) => {
	if (!seconds) return "";
	const minutes = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Verifica se um vídeo do YouTube está disponível usando o backend
 * Inclui verificação de duração (máximo 10 minutos)
 * @param {string} url - URL do vídeo do YouTube
 * @returns {Promise<{available: boolean, title?: string, duration?: number, durationFormatted?: string, errorType?: string, errorMessage?: string}>}
 */
export const checkYouTubeVideoAvailability = async (url, maxDurationSeconds) => {
	if (!isValidYouTubeUrl(url)) {
		return {
			available: false,
			errorType: YouTubeVideoErrorType.UNKNOWN,
			errorMessage: "URL do YouTube inválida",
		};
	}

	try {
		// Usar o service do backend (inclui verificação de duração).
		// maxDurationSeconds opcional: omitido = limite padrão (10min) do back.
		const data = await youtubeService.validate(url, maxDurationSeconds);

		return {
			available: data.available,
			title: data.title,
			author: data.author,
			thumbnail: data.thumbnail,
			duration: data.duration,
			durationFormatted: data.durationFormatted,
			errorType: data.errorType,
			errorMessage: data.errorMessage,
			warning:
				data.errorMessage && data.available
					? data.errorMessage
					: undefined,
		};
	} catch (error) {
		console.warn(
			"Erro ao verificar vídeo via backend, usando fallback:",
			error,
		);
		// Fallback: usar oEmbed diretamente (sem verificação de duração)
		return await checkYouTubeVideoAvailabilityFallback(url);
	}
};

/**
 * Fallback: verifica disponibilidade usando oEmbed (sem verificação de duração)
 * @param {string} url - URL do vídeo
 * @returns {Promise<object>}
 */
const checkYouTubeVideoAvailabilityFallback = async (url) => {
	try {
		const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(
			url,
		)}&format=json`;

		const response = await fetch(oembedUrl);

		if (response.ok) {
			const data = await response.json();
			return {
				available: true,
				title: data.title,
				author: data.author_name,
				thumbnail: data.thumbnail_url,
				warning:
					"Duração não verificada. O vídeo será validado durante o processamento.",
			};
		}

		if (response.status === 401 || response.status === 403) {
			return {
				available: false,
				errorType: YouTubeVideoErrorType.PRIVATE,
				errorMessage: "Este vídeo é privado ou restrito",
			};
		}

		if (response.status === 404) {
			return {
				available: false,
				errorType: YouTubeVideoErrorType.NOT_FOUND,
				errorMessage: "Vídeo não encontrado no YouTube",
			};
		}

		return {
			available: false,
			errorType: YouTubeVideoErrorType.UNAVAILABLE,
			errorMessage: "Vídeo não disponível",
		};
	} catch (error) {
		console.warn(
			"Não foi possível verificar disponibilidade do vídeo (erro de rede):",
			error,
		);
		return {
			available: true,
			warning: "Não foi possível verificar disponibilidade do vídeo",
		};
	}
};

/**
 * Hook personalizado para validação de URL do YouTube com debounce
 * Uso: const { isChecking, result, checkVideo } = useYouTubeValidation();
 */
export const createYouTubeValidator = () => {
	let timeoutId = null;
	let abortController = null;

	return {
		/**
		 * Valida uma URL do YouTube com debounce
		 * @param {string} url - URL para validar
		 * @param {number} debounceMs - Tempo de debounce em ms (default: 500)
		 * @param {function} onStart - Callback quando inicia verificação
		 * @param {function} onResult - Callback com resultado da verificação
		 */
		validate: (url, { debounceMs = 500, onStart, onResult } = {}) => {
			// Cancelar verificação anterior
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (abortController) {
				abortController.abort();
			}

			// URL vazia ou inválida
			if (!url || !isValidYouTubeUrl(url)) {
				if (onResult) {
					onResult(
						url
							? {
									available: false,
									errorType: YouTubeVideoErrorType.UNKNOWN,
									errorMessage: "URL do YouTube inválida",
							  }
							: null,
					);
				}
				return;
			}

			// Debounce
			timeoutId = setTimeout(async () => {
				abortController = new AbortController();

				if (onStart) onStart();

				const result = await checkYouTubeVideoAvailability(url);

				if (onResult) onResult(result);
			}, debounceMs);
		},

		/**
		 * Cancela verificação pendente
		 */
		cancel: () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
			if (abortController) {
				abortController.abort();
				abortController = null;
			}
		},
	};
};

export default {
	extractYouTubeVideoId,
	isValidYouTubeUrl,
	normalizeYouTubeUrl,
	checkYouTubeVideoAvailability,
	createYouTubeValidator,
	YouTubeVideoErrorType,
	MAX_VIDEO_DURATION_SECONDS,
	formatDuration,
};
